import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { Button, Grid, ButtonGroup, Container, Box, Toolbar, Paper, IconButton, TextField, Tooltip } from '@mui/material';

import { useLazyQuery, useMutation } from '@apollo/client';
import i18next from 'i18next';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';

import PagedTable from 'components/table/PagedTable';
import ConfirmationButton from 'components/dialogs/ConfirmationButton';
import { filterSelector } from 'helper/filter';
import { userSelector } from 'helper/security';
import { useDispatch } from 'react-redux';
import { dispatchException, dispatchMessage } from 'helper/snackbar';

import { LIST_HOTEL_GROUPS_QUERY, DELETE_HOTEL_GROUP_MUTATION } from './gql';
import GroupDialog from './GroupDialog';

import { ErrorAlert, RedirectError } from 'pages/error';

export default function Groups() {
  const filter = filterSelector();
  const user = userSelector()!;
  const dispatch = useDispatch();
  
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<{ id: number; name: string } | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  const [query, { data, loading, error }] = useLazyQuery(LIST_HOTEL_GROUPS_QUERY);
  if (error) return <RedirectError err={error} />;

  const [deleteGroup] = useMutation(DELETE_HOTEL_GROUP_MUTATION, {
    onCompleted: () => {
      dispatchMessage(dispatch, i18next.t('groups-deleted-success'));
    },
    onError: (error) => {
      dispatchException(dispatch, error);
    },
    refetchQueries: [
      { query: LIST_HOTEL_GROUPS_QUERY, variables: { spaceId: null } }
    ],
  });

  // Load data on mount
  React.useEffect(() => {
    query({
      variables: {
        spaceId: null,
      },
    });
  }, []);

  const handleCreateGroup = () => {
    setSelectedGroup(null);
    setDialogOpen(true);
  };

  const handleEditGroup = (group: { id: number; name: string }) => {
    setSelectedGroup(group);
    setDialogOpen(true);
  };

  const handleDeleteGroup = async (id: number) => {
    await deleteGroup({
      variables: { id },
    });
  };

  // Filter groups client-side based on search term
  const filteredGroups = React.useMemo(() => {
    if (!data?.listHotelGroups?.hotelGroups) return [];
    if (!searchTerm) return data.listHotelGroups.hotelGroups;

    return data.listHotelGroups.hotelGroups.filter(group =>
      group.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [data?.listHotelGroups?.hotelGroups, searchTerm]);

  return (
    <>
      <Helmet>
        <title>{i18next.t('groups-title')}</title>
      </Helmet>
      <Toolbar />
      <Container component="main" maxWidth="xl">
        <Paper>
          <Grid container direction="column" padding={3} spacing={3}>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, marginBottom: 2 }}>
                <SearchIcon />
                <TextField
                  label={i18next.t('search')}
                  variant="outlined"
                  size="small"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  sx={{
                    minWidth: 300,
                    '& .MuiInputBase-input': { py: 0.5 },
                  }}
                />
              </Box>
              <PagedTable
                filterKey={`${JSON.stringify(filter)}`}
                refetchPage={(skip, take) =>
                  query({
                    variables: {
                      spaceId: null,
                      skip,
                      take,
                    },
                  })
                }
                rowsLoading={loading}
                totalCount={data?.listHotelGroups?.totalCount}
                headers={[
                  i18next.t('groups-name'),
                  i18next.t('groups-assigned-hotels'),
                  i18next.t('created-at'),
                  i18next.t('actions'),
                ]}
                rows={filteredGroups.map((row) => [
                  row.name,
                  row.hotels.length > 0
                    ? row.hotels.map(h => h.name).join(', ')
                    : '-',
                  new Date(row.createdAt).toLocaleDateString(i18next.language === 'en' ? 'en-GB' : 'de-DE'),
                  <Box display="flex" gap={1}>
                    <Tooltip title={i18next.t('edit')}>
                      <IconButton
                        size="small"
                        onClick={() => handleEditGroup({ id: row.id, name: row.name })}
                        sx={{ color: 'text.secondary' }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={i18next.t('delete')}>
                      <span>
                        <ConfirmationButton
                          variant="text"
                          size="small"
                          onConfirm={() => handleDeleteGroup(row.id)}
                          confirmationTitle={i18next.t('groups-delete-confirm-title')}
                          confirmationQuestion={i18next.t('groups-delete-confirm-message', {
                            name: row.name,
                          })}
                          sx={{ minWidth: 'auto', p: 0 }}
                        >
                          <IconButton size="small" sx={{ color: 'text.secondary' }}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </ConfirmationButton>
                      </span>
                    </Tooltip>
                  </Box>,
                ])}
              />
            </Grid>
            {user.isSeminargo && (
              <Grid item xs={12}>
                <ButtonGroup>
                  <Button
                    onClick={handleCreateGroup}
                    variant="contained"
                    startIcon={<AddIcon />}
                  >
                    {i18next.t('groups-create')}
                  </Button>
                </ButtonGroup>
              </Grid>
            )}
          </Grid>
        </Paper>
      </Container>

      {user.isSeminargo && (
        <GroupDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          group={selectedGroup}
          spaceId={null as any}
        />
      )}
    </>
  );
}